import React from "react";
import { Dropdown, DatePicker } from "@my-scoot/component-library-legacy";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { format } from "date-fns";
import classnames from "classnames";

import StatsCard from "common/Components/StatsCard";
import { convertStringToDateObject } from "features/Common/modules/DateTime/utils/DateTime.utils";
import { YEAR_MONTH_DAY_FORMAT } from "features/Common/modules/DateTime/DateTime.constants";
import { date_range_select_options, STAT_CARDS } from "../../constants";
import { useLTOStats } from "./utils/hooks/useLTOStats";
import ExlyImage from "features/Common/modules/ExlyImage/ExlyImage";
import { getS3AssetCDNUrl } from "features/Common/modules/File/modules/Upload/utils/FileUpload.utils";
import {
  formatRevenue,
  SHOW_TOTAL_VISITORS,
} from "features/Common/utils/common.utils";

const StatsLoadingState = ({ color }) => (
  <CircularProgress size={18} color={color} />
);

export const LimitedOfferStats = () => {
  const isDesktop = useMediaQuery("(min-width: 767px)");
  const {
    LTOStatsData,
    dataRange,
    selectedRange,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    onRangeChange,
  } = useLTOStats();

  return (
    <Box className="">
      <div
        className={classnames(
          "tw-mb-[18px] tw-flex",
          selectedRange === date_range_select_options.custom.value
            ? "tw-flex-col md:tw-flex-row tw-gap-3 md:tw-gap-0 md:tw-items-center"
            : "tw-flex-row-reverse md:tw-flex-row tw-justify-between md:tw-justify-normal tw-items-center"
        )}
      >
        <Dropdown
          options={Object.values(date_range_select_options)}
          value={selectedRange}
          handleChange={onRangeChange}
          placeholder="Select date range"
          className={`mr-md-4 !tw-h-[40px] !tw-w-[153px] tw-border-[1.5px] tw-border-solid tw-border-[#cfcdc9] tw-rounded-[5px]`}
        />
        {selectedRange !== date_range_select_options.custom.value ? (
          <p className="!tw-m-0 tw-text-[16px] tw-font-medium tw-tracking-[0.64px] tw-flex tw-items-center">
            {dataRange}
          </p>
        ) : (
          <div className="tw-flex tw-w-full md:tw-w-auto tw-items-center">
            <DatePicker
              size={!isDesktop ? "thin" : "small"}
              popperClassName="!tw-z-[11]"
              placeholder="From date"
              value={
                startDate
                  ? convertStringToDateObject({ dateString: startDate })
                  : null
              }
              onChange={(date) =>
                setStartDate(format(date, YEAR_MONTH_DAY_FORMAT))
              }
              wrapperClassName="tw-w-1/2 md:tw-w-[140px] tw-mr-2 !tw-h-[40px] tw-bg-white"
            />
            <DatePicker
              popperPlacement={isDesktop ? "bottom-end" : ""}
              size={!isDesktop ? "thin" : "small"}
              wrapperClassName={classnames(
                "tw-w-1/2 md:tw-w-[140px] md:tw-ml-2 !tw-h-[40px] tw-bg-white",
                {
                  "!tw-pointer-events-none": !startDate,
                }
              )}
              popperClassName="!tw-z-[11]"
              minDate={
                startDate
                  ? convertStringToDateObject({ dateString: startDate })
                  : null
              }
              disabled={!startDate}
              placeholder="To date"
              value={
                endDate
                  ? convertStringToDateObject({ dateString: endDate })
                  : null
              }
              onChange={(date) =>
                setEndDate(format(date, YEAR_MONTH_DAY_FORMAT))
              }
            />
          </div>
        )}
      </div>
      <div className="tw-flex tw-w-[calc(100vw-32px)] tw-overflow-x-scroll tw-gap-[15px] tw-mb-[26px] md:tw-w-full md:tw-gap-[26px] md:tw-mb-[20px] md:tw-overflow-x-visible">
        {SHOW_TOTAL_VISITORS && (
          <StatsCard
            className="tw-mb-4 md:tw-mb-0"
            color="persian_blue"
            linkText=""
            title={STAT_CARDS.VIEWS.title}
            tooltip="Number of people who visited your webpage or offering pages"
            statsNumber={
              LTOStatsData ? (
                LTOStatsData?.total_visitors
              ) : (
                <StatsLoadingState color="persian_blue" />
              )
            }
            Icon={({ className }) => (
              <PeopleAltIcon
                className={classnames("!tw-text-[32px]", className)}
              />
            )}
          />
        )}
        <StatsCard
          className="tw-mb-4 md:tw-mb-0 !tw-bg-[#E9FBFF]"
          color="primary"
          linkText=""
          title={STAT_CARDS.LEADS.title}
          tooltip="Number of people who showed interest in your webpage or offering pages."
          statsNumber={
            LTOStatsData ? (
              LTOStatsData?.total_leads
            ) : (
              <StatsLoadingState color="primary" />
            )
          }
          Icon={() => (
            <ExlyImage
              src={getS3AssetCDNUrl("assets/vectors/dashboard/leadIcon.png")}
              width={28}
              height={28}
              className="!tw-mr-[16px]"
            />
          )}
          classes={{
            textColorClass: "!tw-text-[#086F86]",
          }}
        />
        <StatsCard
          className="tw-mb-4 md:tw-mb-0"
          color="primary"
          linkText=""
          title={STAT_CARDS.BOOKINGS.title}
          tooltip="Number of bookings received via all tracking links"
          statsNumber={
            LTOStatsData ? (
              LTOStatsData?.total_bookings
            ) : (
              <StatsLoadingState color="primary" />
            )
          }
          Icon={() => (
            <ExlyImage
              src={getS3AssetCDNUrl("assets/vectors/dashboard/booking.png")}
              width={28}
              height={28}
              className="!tw-mr-[16px]"
            />
          )}
        />
        <StatsCard
          className="tw-mb-4 md:tw-mb-0"
          color="fine_pine"
          linkText=""
          title={STAT_CARDS.REVENUE.title}
          tooltip="Total revenue received via all tracking links"
          statsNumber={
            LTOStatsData ? (
              formatRevenue(LTOStatsData?.total_revenue, null, 2)
            ) : (
              <StatsLoadingState color="fine_pine" />
            )
          }
          Icon={() => (
            <ExlyImage
              src={getS3AssetCDNUrl("assets/vectors/dashboard/revenue.png")}
              width={28}
              height={28}
              className="!tw-mr-[16px]"
            />
          )}
        />
      </div>
    </Box>
  );
};
